import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { useAddEducation, useUpdateEducation } from '../hooks';
import type { CandidateEducation } from '../types';

interface EducationFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  candidateId: number;
  education?: CandidateEducation | null;
}

const educationSchema = z.object({
  institution: z.string().min(1, 'L\'établissement est requis').max(255),
  degree: z.string().min(1, 'Le diplôme est requis').max(255),
  field: z.string().max(255).optional().or(z.literal('')),
  startDate: z.string().optional().or(z.literal('')),
  endDate: z.string().optional().or(z.literal('')),
}).refine(
  (data) => {
    if (!data.startDate || !data.endDate) return true;
    return parseInt(data.endDate) >= parseInt(data.startDate);
  },
  { message: 'L\'année de fin doit être postérieure à l\'année de début', path: ['endDate'] }
);

type EducationFormData = z.infer<typeof educationSchema>;

function toYearInput(dateStr: string | null | undefined): string {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '';
  return String(d.getFullYear());
}

export function EducationFormDialog({
  open,
  onOpenChange,
  candidateId,
  education,
}: EducationFormDialogProps) {
  const addEducation = useAddEducation();
  const updateEducation = useUpdateEducation();
  const isEditing = !!education;
  const isPending = addEducation.isPending || updateEducation.isPending;

  const { register, handleSubmit, reset, formState: { errors } } = useForm<EducationFormData>({
    resolver: zodResolver(educationSchema),
    defaultValues: {
      institution: '',
      degree: '',
      field: '',
      startDate: '',
      endDate: '',
    },
  });

  useEffect(() => {
    if (open) {
      if (education) {
        reset({
          institution: education.institution,
          degree: education.degree,
          field: education.field || '',
          startDate: toYearInput(education.startDate),
          endDate: toYearInput(education.endDate),
        });
      } else {
        reset({
          institution: '',
          degree: '',
          field: '',
          startDate: '',
          endDate: '',
        });
      }
    }
  }, [open, education, reset]);

  const onSubmit = async (data: EducationFormData) => {
    const dto = {
      institution: data.institution,
      degree: data.degree,
      field: data.field || undefined,
      startDate: data.startDate ? `${data.startDate}-01-01` : undefined,
      endDate: data.endDate ? `${data.endDate}-01-01` : undefined,
    };

    if (isEditing && education) {
      await updateEducation.mutateAsync({ candidateId, educationId: education.id, dto });
    } else {
      await addEducation.mutateAsync({ candidateId, dto });
    }
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Modifier la formation' : 'Ajouter une formation'}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="institution">Établissement *</Label>
            <Input id="institution" {...register('institution')} placeholder="Nom de l'établissement" />
            {errors.institution && <p className="text-xs text-destructive">{errors.institution.message}</p>}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="degree">Diplôme *</Label>
              <Input id="degree" {...register('degree')} placeholder="Ex: Master Informatique" />
              {errors.degree && <p className="text-xs text-destructive">{errors.degree.message}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="field">Spécialité</Label>
              <Input id="field" {...register('field')} placeholder="Ex: Intelligence Artificielle" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="startDate">Année de début</Label>
              <Input
                id="startDate"
                type="number"
                min="1950"
                max="2099"
                {...register('startDate')}
                placeholder="2020"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="endDate">Année de fin</Label>
              <Input
                id="endDate"
                type="number"
                min="1950"
                max="2099"
                {...register('endDate')}
                placeholder="2023"
              />
              {errors.endDate && (
                <p className="text-xs text-destructive">{errors.endDate.message}</p>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Annuler
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isEditing ? 'Modifier' : 'Ajouter'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
